import { t } from '@sportninja/common/i18n'
import { isValidEmail } from '@sportninja/common/utils/utils'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import FormInput from '../Form/FormInput'
import ModernPhoneInput from '../ts-components/ModernPhoneInput'

/**
 * InvitationFormField component for capturing email and phone information
 * Used in invitation flows to collect contact details for new users
 * @param {Object} props - Component props
 * @param {Function} props.onChange - Callback when field values change (required)
 * @param {string} props.namePhone - Field name for phone input (required)
 * @param {string} props.nameEmail - Field name for email input (required)
 * @param {string} [props.defaultValuePhone] - Default phone value
 * @param {string} [props.defaultValueEmail] - Default email value
 * @param {boolean} [props.disabledPhone] - Whether phone input is disabled
 * @param {boolean} [props.disabledEmail] - Whether email input is disabled
 * @param {boolean} [props.hasErrorPhone] - Whether phone input has error
 * @param {boolean} [props.hasErrorEmail] - Whether email input has error
 * @param {boolean} [props.hideHelpPhone] - Whether to hide phone help text
 * @param {boolean} [props.hideHelpEmail] - Whether to hide email help text
 * @param {boolean} [props.requiredPhone] - Whether phone is required
 * @param {boolean} [props.requiredEmail] - Whether email is required
 * @param {React.ReactNode} [props.customHelpPhone] - Custom help content for phone
 * @param {React.ReactNode} [props.customHelpEmail] - Custom help content for email
 * @param {Object} [props.errors] - Validation errors object
 * @returns {JSX.Element|null} InvitationFormField component or null if required props missing
 */
const InvitationFormField = ({
  customHelpPhone,
  customHelpEmail,
  defaultValuePhone,
  defaultValueEmail,
  disabledPhone,
  disabledEmail,
  hasErrorPhone,
  hasErrorEmail,
  hideHelpPhone,
  hideHelpEmail,
  namePhone,
  nameEmail,
  requiredPhone,
  requiredEmail,
  onChange,
  errors,
  ...props
}) => {
  const [phoneValue, setPhoneValue] = React.useState(defaultValuePhone || '')
  const [isPhoneInvalid, setIsPhoneInvalid] = React.useState(false)

  const [emailValue, setEmailValue] = React.useState(defaultValueEmail)

  const [invalidEmailError, setInvalidEmailError] = React.useState(false)

  useEffect(() => {
    if (defaultValueEmail) {
      onChangeEmail({ target: { name: nameEmail, value: defaultValueEmail } })
    }
  }, [defaultValueEmail, nameEmail])

  useEffect(() => {
    if (emailValue) {
      setInvalidEmailError(!isValidEmail(emailValue))
    }
  }, [emailValue])

  const handlePhoneChange = (value) => {
    setPhoneValue(value)
    onChange?.({ target: { name: namePhone, value } })
  }

  const handleCountryChange = (country) => {
    // Derive country field name: phone_number -> phone_number_country
    const countryFieldName = `${namePhone}_country`
    onChange?.({ target: { name: countryFieldName, value: country || 'US' } })
  }

  const onChangeEmail = ({ target }) => {
    onChange?.({ target })
    setEmailValue(target.value)
  }

  // Validate required props after hooks are called
  if (!onChange || !namePhone || !nameEmail) {
    console.error(
      'InvitationFormField: Missing required props (onChange, namePhone, or nameEmail)'
    )
    return null
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          gap: 16,
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <FormInput
          onChange={onChangeEmail}
          disabled={disabledEmail}
          hasError={hasErrorEmail || invalidEmailError}
          input={{
            defaultValue: defaultValueEmail,
            name: nameEmail,
            label: t('common:email'),
            placeholder: 'patq@sportninja.com',
            type: 'email',
            required: requiredEmail,
          }}
        />
        <ModernPhoneInput
          label={t('common:phone')}
          value={phoneValue}
          onChange={handlePhoneChange}
          onCountryChange={handleCountryChange}
          disabled={disabledPhone}
          error={
            errors?.[namePhone]
              ? errors[namePhone]
              : isPhoneInvalid
              ? 'Invalid phone number'
              : undefined
          }
          tabIndex={1}
          validateOnBlur={true}
          onValidationChange={(isValid) => {
            setIsPhoneInvalid(!isValid)
          }}
          variant='legacy'
          hideErrorLabel={true}
        />
      </div>
      {!hideHelpEmail && (
        <p style={{ marginTop: 16, fontSize: 13 }}>
          {t('Web:inviteToJoinDescription')} {t('Web:invitedToRegister')}
        </p>
      )}
      {customHelpEmail}
      {!hideHelpPhone && (
        <p style={{ marginTop: 16, fontSize: 13 }}>
          Add your phone number to receive SMS notifications.
        </p>
      )}
      {customHelpPhone}
    </>
  )
}

InvitationFormField.propTypes = {
  onChange: PropTypes.func.isRequired,
  namePhone: PropTypes.string.isRequired,
  nameEmail: PropTypes.string.isRequired,
  defaultValuePhone: PropTypes.string,
  defaultValueEmail: PropTypes.string,
  disabledPhone: PropTypes.bool,
  disabledEmail: PropTypes.bool,
  hasErrorPhone: PropTypes.bool,
  hasErrorEmail: PropTypes.bool,
  hideHelpPhone: PropTypes.bool,
  hideHelpEmail: PropTypes.bool,
  requiredPhone: PropTypes.bool,
  requiredEmail: PropTypes.bool,
  customHelpPhone: PropTypes.node,
  customHelpEmail: PropTypes.node,
  errors: PropTypes.object,
}

export default InvitationFormField
